import * as React from 'react';

import { useQuery } from '@apollo/client';

import { ModalContext, Text } from '@notino/react-styleguide';
import { AddToCartSettingsQuery } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';

import {
  AddToCartButtonProvider,
  useAddToCartButtonContext,
  useIsMouseOverBuyButton,
} from './context';
import { IAddToCartButtonProps } from './model';
import getAddToCartSettings from './queries/addToCartSettings.graphql';
import { BuyButton } from './sharedComponents/BuyButton';
import { SpecialAddToCartModal } from './SpecialAddToCartModal';
import { useMessagesForSpecialModal } from './SpecialAddToCartModal/useMessagesForSpecialModal';
import { ButtonWrapper } from './styled';

export const AddToCartCoreComponent: React.FC<IAddToCartButtonProps> =
  React.memo((props) => {
    const { data } = useQuery<AddToCartSettingsQuery>(getAddToCartSettings);
    const { toggleModal } = ModalContext.useModalContext();

    const {
      props: {
        product,
        buttonWrapper: WrapperComponent = ButtonWrapper,
        buttonWrapperProps,
      },
    } = useAddToCartButtonContext();

    const mouseOverBuyButton = useIsMouseOverBuyButton();

    const settings = data?.Settings;
    const { isSpecial, description, header } =
      useMessagesForSpecialModal(product);

    const handleShowSpecialModal = () => {
      toggleModal(
        <AddToCartButtonProvider {...props}>
          <SpecialAddToCartModal
            description={description}
            productId={product.id}
            productCode={product.productCode}
            settings={settings}
          />
        </AddToCartButtonProvider>,
        {
          header: <Text typography="titleLarge">{header}</Text>,
          center: true,
          positionBottomOnMobile: true,
        }
      );
    };

    return (
      <ErrorBoundary>
        <WrapperComponent {...buttonWrapperProps}>
          <BuyButton
            productId={product.id}
            productCode={product.productCode}
            isSpecialProduct={isSpecial}
            Settings={settings}
            buttonType={mouseOverBuyButton ? 'button' : 'submit'}
            showSpecialModal={handleShowSpecialModal}
          />
        </WrapperComponent>
      </ErrorBoundary>
    );
  });
